import {
  ANALYTICS_GRANULARITIES,
  type AnalyticsDimension,
  type AnalyticsGranularity,
  type AnalyticsMode,
} from "@loot-os/api-contract";
import { sql } from "drizzle-orm";
import { bigint, index, integer, pgTable, primaryKey, text, timestamp } from "drizzle-orm/pg-core";

// ---- analytics ------------------------------------------------------------------
// Raw events never live in Postgres: they stream through Firehose into the Iceberg table
// `analytics.events` on S3 and are queried with Athena (docs/analytics.md). These tables hold
// what the dashboard counts (aggregates), what the crons record about themselves, and the
// tombstones of deleted environments. The dashboard's event log reads raw events from Athena,
// not from here (local development aside).

/** `running` is set when a run starts (scheduled or on demand) and replaced when it ends. */
export const ANALYTICS_ROLLUP_STATUSES = ["ok", "failed", "running"] as const;
export type AnalyticsRollupStatus = (typeof ANALYTICS_ROLLUP_STATUSES)[number];

/**
 * One bucket of one event name in one environment: how many distinct event ids Athena counted
 * and how many distinct shoppers (end user id, else anonymous id) sent them. A full recompute
 * of a window overwrites its rows, so the table is idempotent under re-runs.
 *
 * Writer: the analytics rollup crons only. No foreign keys: an environment's rows are deleted
 * with it, explicitly, and its id becomes a tombstone (`analytics_purges`) that the writer
 * checks before every upsert. Whole row is safe to return to tenant members.
 */
export const analyticsRollups = pgTable(
  "analytics_rollups",
  {
    environmentId: text("environment_id").notNull(),
    tenantId: text("tenant_id").notNull(),
    granularity: text("granularity", { enum: ANALYTICS_GRANULARITIES })
      .$type<AnalyticsGranularity>()
      .notNull(),
    /** The bucket's start, UTC, on an hour or a day boundary. */
    bucket: timestamp("bucket", { withTimezone: true }).notNull(),
    eventName: text("event_name").notNull(),
    /** Distinct event ids in the bucket. */
    events: integer("events").notNull(),
    /** Distinct shoppers in the bucket; never summed across buckets. */
    users: integer("users").notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // Named by hand: drizzle's default would exceed Postgres's 63-character identifier limit.
    primaryKey({
      name: "analytics_rollups_pk",
      columns: [t.environmentId, t.granularity, t.bucket, t.eventName],
    }),
    // The hourly prune deletes by granularity and age across every environment.
    index("analytics_rollups_granularity_bucket_idx").on(t.granularity, t.bucket),
  ],
);

/**
 * One bucket of one event name in one environment, under one value of one dimension
 * (`ANALYTICS_DIMENSIONS`): the same two counts as `analytics_rollups`, split by the value the
 * event was stamped with. One dimension per row, never a combination, so the table grows with
 * the values seen and not with their product. An event with an empty column is counted under
 * `unknown`, so a dimension's `events` add up to the rollup row's; its `users` do not, because
 * one shopper can be under two values in one bucket. The all-events name `*` has rows too.
 *
 * Writer: the analytics rollup crons only, in the transaction that writes `analytics_rollups`,
 * under the same tombstone check. No foreign keys, deleted with the environment like the
 * rollups. Whole row is safe to return to tenant members.
 */
export const analyticsRollupBreakdowns = pgTable(
  "analytics_rollup_breakdowns",
  {
    environmentId: text("environment_id").notNull(),
    tenantId: text("tenant_id").notNull(),
    granularity: text("granularity", { enum: ANALYTICS_GRANULARITIES })
      .$type<AnalyticsGranularity>()
      .notNull(),
    /** The bucket's start, UTC, on an hour or a day boundary. */
    bucket: timestamp("bucket", { withTimezone: true }).notNull(),
    eventName: text("event_name").notNull(),
    dimension: text("dimension").$type<AnalyticsDimension>().notNull(),
    /** The stamped value, or `unknown` for an empty column. */
    value: text("value").notNull(),
    /** Distinct event ids in the bucket under this value. `bigint`: a new table is free to be. */
    events: bigint("events", { mode: "number" }).notNull(),
    /** Distinct shoppers in the bucket under this value; never summed, across buckets or values. */
    users: bigint("users", { mode: "number" }).notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    primaryKey({
      name: "analytics_rollup_breakdowns_pk",
      columns: [t.environmentId, t.granularity, t.bucket, t.eventName, t.dimension, t.value],
    }),
    // The hourly prune deletes by granularity and age across every environment.
    index("analytics_rollup_breakdowns_granularity_bucket_idx").on(t.granularity, t.bucket),
  ],
);

/**
 * When each rollup last ran and how it ended, one row per granularity. The dashboard shows the
 * `hour` and `day` rows as "updated at" and a `running` hour row as "computing"; a `failed` row
 * also reaches Sentry through the cron's monitor. Writer: the rollup crons and the dashboard's
 * on-demand recompute (`running` only). Safe to return.
 */
export const analyticsRollupState = pgTable("analytics_rollup_state", {
  granularity: text("granularity", { enum: ANALYTICS_GRANULARITIES })
    .$type<AnalyticsGranularity>()
    .primaryKey(),
  lastRunAt: timestamp("last_run_at", { withTimezone: true }).notNull(),
  status: text("status", { enum: ANALYTICS_ROLLUP_STATUSES }).notNull(),
  error: text("error"),
});

/**
 * A tombstone per tenant or sandbox deletion: the environment ids whose raw events the daily
 * cron deletes from the Iceberg table, and that no rollup may ever be written for again. Rows
 * are inserted in the deleting transaction, completed by the cron, and never deleted, because
 * the rollup writer's exclusion is what keeps a deleted environment from coming back.
 *
 * Writer: deleteTenant / deleteSandbox (insert), the daily analytics cron (completed_at, error).
 * No foreign keys: the tenant row is gone by the time the cron reads this. Platform-only.
 */
export const analyticsPurges = pgTable(
  "analytics_purges",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id").notNull(),
    environmentIds: text("environment_ids").array().notNull(),
    requestedAt: timestamp("requested_at", { withTimezone: true }).notNull().defaultNow(),
    completedAt: timestamp("completed_at", { withTimezone: true }),
    error: text("error"),
  },
  (t) => [
    index("analytics_purges_pending_idx").on(t.requestedAt).where(sql`${t.completedAt} is null`),
  ],
);

/**
 * Local development only: the raw events the dev sink stores so "Recompute now", the event log
 * and the tests can read them without Athena. The columns are the Iceberg table's (`ANALYTICS_COLUMNS`) and
 * the rollup over it has the Athena query's semantics, so what a developer sees locally is what
 * a stage computes. Empty on every deployed stage: the Firehose sink never writes here, and no
 * cron reads it.
 *
 * Writer: the local analytics sink (`npm run dev`, tests). Holds the pseudonymous identifiers
 * (anonymous id, session id, user agent): returned only by the event log (`analytics.events`,
 * tenant admins, the row's own environment), never at viewer level and never on `/v1`.
 */
export const analyticsEventsLocal = pgTable(
  "analytics_events_local",
  {
    id: text("id").primaryKey(),
    tenantId: text("tenant_id").notNull(),
    environmentId: text("environment_id").notNull(),
    endUserId: text("end_user_id"),
    anonymousId: text("anonymous_id"),
    sessionId: text("session_id"),
    eventName: text("event_name").notNull(),
    source: text("source").notNull(),
    occurredAtMs: bigint("occurred_at_ms", { mode: "number" }).notNull(),
    receivedAtMs: bigint("received_at_ms", { mode: "number" }).notNull(),
    dt: text("dt").notNull(),
    route: text("route"),
    userAgent: text("user_agent"),
    properties: text("properties").notNull(),
    os: text("os"),
    device: text("device"),
    country: text("country"),
    segment: text("segment"),
  },
  (t) => [index("analytics_events_local_env_occurred_idx").on(t.environmentId, t.occurredAtMs)],
);

/** The column the environments table stores the mode in, typed for callers that read the row. */
export type EnvironmentAnalyticsMode = AnalyticsMode;
